"use client";

import { useMessages } from "@/lib/i18n/useMessages";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { important } from "@nvon/react-toolbox";
import Image from "next/image";
import { useState } from "react";
import { SLDialog } from "../dialogs/SLDialog";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

export const bluryWhiteBgProps = {
  backdropFilter: "blur(16px)",
  backgroundColor: "rgba(255, 255, 255, 0.08)",
  boxShadow: "rgba(16, 24, 40, 0.04) 0px 1px 2px 0px",
  border: "1px solid rgba(255, 255, 255, .16)",
};

export const SLTestimonialCard = ({
  image,
  logo,
  citation,
  name,
  role,
  longDescription,
}: {
  /** Optional; reserved for staggered animation when wired from a carousel */
  index?: number;
  image?: string;
  logo?: string;
  citation: string;
  name: string;
  role?: string;
  longDescription?: string;
}) => {
  const theme = useTheme();
  const home = useMessages().home;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  // Truncate long description for preview (show first 150 characters)
  const lengthOfTruncation = 110;
  const truncatedDescription =
    longDescription && longDescription.length > lengthOfTruncation
      ? longDescription.substring(0, lengthOfTruncation) + "..."
      : longDescription;

  const shouldShowButton =
    longDescription && longDescription.length > lengthOfTruncation;

  const isXs = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });

  return (
    <Box sx={{ height: "100%", px: isXs ? 1 : 0 }}>
      <Card
        sx={{
          boxShadow: "none",
          //   border: `1px solid ${theme.palette.warning.dark}`,
          minHeight: {
            lg: "400px",
            md: "300px",
            sm: "undefined",
          },
          height: "100%",
          backgroundColor: (th) => th.palette.background.paper,
          //   ...bluryWhiteBgProps,
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            width: "100%",
            p: "0 !important",
            m: 0,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={{ sm: 1, md: 2 }}
            height={"100%"}
            width={"100%"}
            py={2}
          >
            {image && (
              <Box
                sx={{
                  position: "relative",
                  minHeight: { sm: "200px", md: "400px", lg: "500px" },
                  height: "100%",
                  aspectRatio: "3/4",
                  p: 0,
                  borderRadius: "20px",
                  overflow: "clip",
                  background: (th) => th.palette.secondary.dark,
                }}
              >
                <Image
                  src={image}
                  fill={true}
                  style={{ objectFit: "cover" }}
                  alt={name}
                />
              </Box>
            )}

            <Stack
              px={isMobile ? theme.spacing(2) : theme.spacing(4)}
              width={"100%"}
              height={"100%"}
              gap={theme.spacing(2)}
              sx={{
                alignItems: "space-between",
              }}
            >
              <Stack
                key={name}
                width={"100%"}
                height={"fit-content"}
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                {(image || logo) && (
                  <Stack
                    direction={"row"}
                    gap={1}
                    width={"100%"}
                    height={"fit-content"}
                    justifyContent={"space-between"}
                    sx={{ maxHeight: "40px" }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxHeight: "40px",
                      }}
                      position={"relative"}
                    >
                      {logo && (
                        <Image
                          src={logo}
                          fill={true}
                          alt="Logo"
                          style={{
                            objectFit: "contain",
                            objectPosition: "left",
                          }}
                        />
                      )}
                    </Box>

                    {/* <Image
                    src="/images/icons/left-quote.svg"
                    width={isMobile ? 50 : 100}
                    height={isMobile ? 50 : 100}
                    alt="Quotation mark"
                    style={{
                      opacity: 0.8,
                    }}
                  /> */}
                  </Stack>
                )}

                <Stack
                  width={"100%"}
                  height={"fit-content"}
                  sx={{
                    alignItems: image ? "flex-start" : "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography
                    variant="h5"
                    component={"p"}
                    textAlign={image ? "left" : "center"}
                    sx={{
                      color: (th) => th.palette.text.primary,
                      maxWidth: image || isMobile ? "100%" : "100%",
                      py: isMobile ? 1 : 3,
                      // fontStyle: "italic",
                    }}
                  >
                    <SLHtmlFromLangFile htmlString={citation} />
                  </Typography>
                </Stack>
              </Stack>
              <Stack height={"100%"} justifyContent={"space-between"}>
                {longDescription && (
                  <Stack
                    sx={{
                      width: "100%",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: theme.spacing(2),
                      display: !shouldShowButton ? "flex" : "inline-block",
                    }}
                  >
                    <Typography
                      component={"div"}
                      variant="body1"
                      sx={{
                        color: (th) => th.palette.text.secondary,
                        pb: 2,
                      }}
                    >
                      {truncatedDescription}
                      {shouldShowButton && (
                        <Button
                          component={"span"}
                          variant="text"
                          color="primary"
                          size="small"
                          onClick={handleDialogOpen}
                          sx={{
                            padding: important("3px 12px"),
                          }}
                        >
                          {home.testimonials.showMore}
                        </Button>
                      )}
                    </Typography>
                  </Stack>
                )}
                <Stack
                  height={"fit-content"}
                  alignItems={"flex-end"}
                  justifyContent={"flex-end"}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: (th) => th.palette.text.secondary,
                    }}
                  >
                    {name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: (th) => th.palette.text.secondary,
                      opacity: 0.7,
                    }}
                  >
                    {role}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Dialog for full description */}
      <SLDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={<SLHtmlFromLangFile htmlString={citation} />}
        subtitle={`${name} (${role})`}
        content={
          <Stack gap={theme.spacing(2)}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.6,
              }}
            >
              {`„${longDescription}“`}
            </Typography>
          </Stack>
        }
      />
    </Box>
  );
};
